// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeSheet.tsx
// 🔧 PURPOSE: Il foglio dal basso della Home, nello stile nuovo: avorio con
//             il bordo sabbia (stone-900 in scuro), occhiello d'oro, titolo
//             in Fraunces, la X da 44px. Lo usano la scelta "Come vuoi
//             scriverci?" (Assistente AI o reception) e "Esperienze e
//             noleggi" nelle viste ospite e prima dell'arrivo.
//
//             Portale su <body> (fuori dal contenitore che scorre), sopra la
//             barra in basso. Esc chiude, Tab resta nel foglio, il focus
//             parte dalla X e alla chiusura torna a chi l'ha aperto (come
//             VipTicketSheet). La pagina sotto non scorre finché è aperto.
//
//             useHomeSheetState: un foglio che apre pagine ("Esperienze e
//             noleggi") si ritrova tornando indietro. La pagina aperta dal
//             foglio riscrive prima la voce della Home con ?sheet=<nome>
//             (openService con `backTo`); la Home rimontata su quel segno
//             riapre il foglio, e chiudendolo il segno si toglie (un reload
//             non lo riapre). La scheda Home della barra porta a "/": chiuso.
// ============================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useStatusBarVeil } from '../../lib/statusBar';
import { homeSheetOf, withHomeSheet } from '../../lib/servicePages';
import { Eyebrow, FOCUS_RING, INK_TEXT } from './ui';

interface HomeSheetProps {
  open: boolean;
  onClose: () => void;
  /** Il titolo del foglio (anche il nome del dialogo per i lettori di schermo). */
  title: React.ReactNode;
  /** L'occhiello d'oro sopra il titolo, facoltativo. */
  eyebrow?: React.ReactNode;
  children: React.ReactNode;
  /** L'id del titolo (aria-labelledby); uno per foglio. */
  titleId: string;
  className?: string;
}

export interface HomeSheetState {
  open: boolean;
  show: () => void;
  /** Chiude e toglie il segno dall'indirizzo, se c'è. */
  close: () => void;
  /** La voce della Home col segno del foglio: il `backTo` delle pagine aperte da lì. */
  returnTo: string;
}

/**
 * Aperto/chiuso di un foglio della Home che si ritrova tornando indietro
 * (vedi in cima). `enabled` falso: il foglio non c'è, il segno non conta.
 */
export function useHomeSheetState(name: string, enabled: boolean = true): HomeSheetState {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => enabled && homeSheetOf(location.search) === name);
  const { pathname, search } = location;
  const show = useCallback(() => setOpen(true), []);
  const close = useCallback(() => {
    setOpen(false);
    if (homeSheetOf(search) !== name) return;
    // Una pagina già partita dal foglio (il suo pezzo si scarica, la Home è
    // ancora qui): la voce di adesso è la sua, non si riscrive.
    if (typeof window !== 'undefined' && window.location.pathname !== pathname) return;
    navigate(withHomeSheet(pathname, search, null), { replace: true });
  }, [name, navigate, pathname, search]);
  return { open: enabled && open, show, close, returnTo: withHomeSheet(pathname, search, name) };
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const HomeSheet: React.FC<HomeSheetProps> = ({ open, onClose, title, eyebrow, children, titleId, className }) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // La barra di stato si scurisce come la pagina sotto il velo (lib/statusBar).
  useStatusBarVeil(0.45, open);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.prototype.slice.call(panelRef.current.querySelectorAll(FOCUSABLE)) as HTMLElement[];
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    // La pagina sotto resta ferma: il body e i contenitori che scorrono.
    const roots = Array.prototype.slice.call(document.querySelectorAll('[data-scroll-root]')) as HTMLElement[];
    const previousRoots = roots.map((el) => el.style.overflowY);
    const previousBody = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    roots.forEach((el) => { el.style.overflowY = 'hidden'; });

    try {
      closeRef.current?.focus({ preventScroll: true });
    } catch {
      closeRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousBody;
      roots.forEach((el, index) => { el.style.overflowY = previousRoots[index]; });
      // Il focus torna a chi ha aperto, se c'è ancora (un'altra finestra
      // aperta dal foglio se lo prende da sola subito dopo).
      if (opener && opener.isConnected && typeof opener.focus === 'function') {
        try {
          opener.focus({ preventScroll: true });
        } catch {
          /* elemento sparito */
        }
      }
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[200]" role="presentation">
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('common.close')}
        onClick={() => onCloseRef.current()}
        className="absolute inset-0 h-full w-full cursor-default bg-black/45 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'absolute bottom-0 left-0 right-0 max-h-[88vh] overflow-y-auto rounded-t-[24px] border-t border-nva-edge bg-nva-bone px-4 pt-2 shadow-2xl',
          'dark:border-stone-800 dark:bg-stone-900',
          'motion-safe:animate-in motion-safe:slide-in-from-bottom-8 motion-safe:fade-in motion-safe:duration-200',
          className,
        )}
        style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', overscrollBehavior: 'contain' }}
      >
        <span aria-hidden="true" className="mx-auto mb-2 block h-1 w-10 rounded-full bg-nva-edge dark:bg-stone-700" />
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1 pt-1">
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            <h2 id={titleId} className={cn('font-display text-[21px] font-semibold leading-tight', INK_TEXT)}>
              {title}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={() => onCloseRef.current()}
            aria-label={t('common.close')}
            className={cn(
              'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-nva-edge bg-nva-card text-nva-espresso',
              'dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100',
              FOCUS_RING,
            )}
          >
            <X className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default HomeSheet;
